// Slice D4 — <CanvasComboboxField /> provenance-aware make/model combobox.
//
// Wraps <BrandCombobox /> in the SAME provenance envelope CanvasSelectField
// uses (FieldTintWrapper + FormField + ProvenanceChip +
// ConflictChooser) so an AI-filled make/model keeps its violet chip and
// conflict chooser — and selecting/accepting flips it to "✓ Edited by you"
// via markEdited(). Options + the "did you mean?" suggestion are sourced by
// fieldName: make → manufacturer catalog; model → make-scoped model catalog.

import { useFormContext, useWatch } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@marine/ui/form';
import type { ProvenanceEntry } from '@/modules/inventory/types/boat.types';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';
import { useManufacturerOptions } from '@/modules/inventory/hooks/useManufacturerOptions';
import { useModelOptions } from '@/modules/inventory/hooks/useModelOptions';
import { useBrandSuggestion } from '@/modules/inventory/hooks/useBrandSuggestion';
import { FieldTintWrapper } from '../FieldTintWrapper';
import { ProvenanceChip } from '../ProvenanceChip';
import { ConflictChooser } from '../ConflictChooser';
import { FieldNotices } from '../CanvasReview';
import { useProvenance } from '../hooks/useProvenance';
import { sortByAuthority } from '../lib/sourceAuthority';
import { BrandCombobox } from './BrandCombobox';

interface CanvasComboboxFieldProps {
  /** A top-level WizardFormData brand key. */
  fieldName: 'manufacturer' | 'model';
  label: string;
  placeholder?: string;
  required?: boolean;
}

export function CanvasComboboxField({
  fieldName,
  label,
  placeholder,
  required = false,
}: CanvasComboboxFieldProps) {
  const { control } = useFormContext<WizardFormData>();
  const { state, clear, selectSource, markEdited } = useProvenance(fieldName);

  const isModel = fieldName === 'model';
  const make = (useWatch({ control, name: 'manufacturer' }) as string) || '';
  const value = (useWatch({ control, name: fieldName }) as string) || '';

  const mfrOptions = useManufacturerOptions();
  const modelOptions = useModelOptions(make, isModel);
  const options = (isModel ? modelOptions.data : mfrOptions.data) ?? [];
  const loading = isModel ? modelOptions.isLoading : mfrOptions.isLoading;

  const suggestion = useBrandSuggestion(
    fieldName,
    value,
    isModel ? make : undefined,
  );

  return (
    <FieldTintWrapper fieldName={fieldName}>
      <FormField
        control={control}
        name={fieldName as never}
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between gap-2">
              <FormLabel className="text-xs">
                {label}
                {required && <span className="text-destructive ml-0.5">*</span>}
              </FormLabel>
              <ProvenanceChip
                fieldName={fieldName}
                entry={state.entry}
                onClear={clear}
                isLowConfidence={state.isLowConfidence}
              />
            </div>
            {state.isConflict && Array.isArray(state.entry) && (
              <ConflictChooser
                fieldName={fieldName}
                variant="radio"
                entries={sortByAuthority(state.entry as ProvenanceEntry[])}
                selectedIndex={state.pickedIndex}
                onSelectOne={(entry) => selectSource(entry)}
              />
            )}
            <FieldNotices fieldName={fieldName} />
            <FormControl>
              <BrandCombobox
                value={(field.value as string) || ''}
                onChange={(v) => {
                  field.onChange(v);
                  // Editing an AI-filled field is the accept gesture (mirror
                  // CanvasSelectField): flip violet "AI" → "✓ Edited by you".
                  markEdited();
                }}
                options={options}
                loading={loading}
                placeholder={placeholder}
                suggestion={suggestion}
                onAcceptSuggestion={(name) => {
                  field.onChange(name);
                  markEdited();
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </FieldTintWrapper>
  );
}
